import { WizardAPI as API } from "../config/_axios_config";
import {
  CrawlerAdapterResultRecord,
  CrawlerRunDetail,
  HealthResponse,
  HistoryResponse,
} from "../types/crawlerHealth";
import { Adapter, ConstraintV2, ConstraintV2Draft } from "../types/adapter";
import { Badge } from "../types/badge";
import { Integration } from "../types/integration";
import { Mapping } from "../types/mapping";
import { SpmInfo } from "../types/spmInfo";
import {
  SpmCrawlResult,
  SpmIntegrationDraft,
  SpmIntegrationInfo,
  SpmIntegrationRecord,
} from "../types/spmIntegrationInfo";
import {
  IntegrationType,
  NativeMediationsResponse,
  NativeSdksResponse,
  PlatformSelection,
  SdksRequest,
  SpmCoverageResponse,
  SpmDependency,
  UnityMediationsResponse,
  UnitySdksResponse,
} from "../types/nativeIntegration";
import { SKAdNetwork } from "../types/skadn";
import { AdAttributionKit } from "../types/aak";
import { PageResult } from "../types/page";
import { Platform } from "../types/platform";
import { Sdk } from "../types/sdk";
import { Plugin } from "../types/plugin";
import { AvailableVersionsResponse, MediationType, ValidationResponse, VersionInfo } from "../types/validator";
import {
  AppodealVersionsResponse,
  RecommendationsResponse,
  CreateRecommendationRequest,
  CopyRecommendationsResponse,
} from "../types/recommendations";

class ApiComponent<T> {
  readonly path: string;

  constructor(path: string) {
    this.path = path;
  }

  all(searchParams?: URLSearchParams): Promise<PageResult<T>> {
    return API.get(this.path + "?" + searchParams?.toString()).then(
      (result) => result.data
    );
  }

  create(model: T): Promise<T> {
    return API.post(this.path, model).then((response) => response.data);
  }

  one(id: number): Promise<T> {
    return API.get(this.path + "/" + id.toString()).then(
      (response) => response.data
    );
  }

  update(id: number, model: T): Promise<T> {
    return API.patch(this.path + "/" + id.toString(), model).then(
      (response) => response.data
    );
  }

  delete(id: number): Promise<void> {
    return API.delete(this.path + "/" + id.toString());
  }

  copy(id: number): Promise<T> {
    return API.post(this.path + "/" + id.toString() + "/copy").then(
      (response) => response.data
    );
  }
}

class SdkApiComponent extends ApiComponent<Sdk> {
  constructor() {
    super("v4/sdk");
  }

  copy(id: number): Promise<Sdk> {
    return API.post(this.path + "/" + id.toString() + "/copy").then(
      (response) => response.data
    );
  }

  latest(status: string, platform: Platform): Promise<Sdk> {
    return API.get(
      this.path + "/latest/" + status + "/" + platform.toString()
    ).then((response) => response.data);
  }

  latestVersions(status: string, platform: Platform, count: number = 5): Promise<Sdk[]> {
    return API.get(
      this.path + "/latest/" + status + "/" + platform.toString() + "/versions?count=" + count
    ).then((response) => response.data);
  }

  integration(id: number): Promise<Integration[]> {
    return API.get(this.path + "/" + id.toString() + "/code").then(
      (response) => response.data
    );
  }

  addIntegration(id: number, model: Integration): Promise<Integration[]> {
    return API.post(this.path + "/" + id.toString() + "/code", model).then(
      (response) => response.data
    );
  }

  removeIntegration(id: number, integratonId: number): Promise<Integration[]> {
    return API.delete(
      this.path + "/" + id.toString() + "/code/" + integratonId.toString()
    ).then((response) => response.data);
  }

  adapters(id: number): Promise<Adapter[]> {
    return API.get(this.path + "/" + id.toString() + "/adapter").then(
      (response) => response.data
    );
  }

  addAdapter(id: number, model: Adapter): Promise<Adapter[]> {
    return API.post(this.path + "/" + id.toString() + "/adapter", model).then(
      (response) => response.data
    );
  }

  removeAdapter(id: number, adapterId: number): Promise<Adapter[]> {
    return API.delete(
      this.path + "/" + id.toString() + "/adapter/" + adapterId.toString()
    ).then((response) => response.data);
  }

  suggestedAdapters(id: number): Promise<Adapter[]> {
    return API.get(this.path + "/" + id.toString() + "/adapter/suggested").then(
      (response) => response.data
    );
  }

  badges(id: number): Promise<Badge[]> {
    return API.get("v4/sdk/" + id.toString() + "/badge").then(
      (response) => response.data
    );
  }

  spmIntegrations(id: number): Promise<SpmIntegrationInfo[]> {
    return API.get(this.path + "/" + id.toString() + "/spm").then(
      (response) => response.data
    );
  }
}

class SKAdNetworkApiComponent extends ApiComponent<SKAdNetwork> {
  constructor() {
    super("v4/skadn");
  }

  copy(id: number): Promise<SKAdNetwork> {
    return API.post(this.path + "/" + id.toString() + "/copy").then(
      (response) => response.data
    );
  }

  synchronize(
    searchParams?: URLSearchParams
  ): Promise<PageResult<SKAdNetwork>> {
    return API.post(this.path + "/sync" + "?" + searchParams?.toString()).then(
      (result) => result.data
    );
  }

  ids(): Promise<string[]> {
    return API.get(this.path + "/ids").then((response) => response.data);
  }
}

class AdAttributionKitApiComponent extends ApiComponent<AdAttributionKit> {
  constructor() {
    super("v4/aak");
  }

  copy(id: number): Promise<AdAttributionKit> {
    return API.post(this.path + "/" + id.toString() + "/copy").then(
      (response) => response.data
    );
  }

  synchronize(
    searchParams?: URLSearchParams
  ): Promise<PageResult<AdAttributionKit>> {
    return API.post(this.path + "/sync" + "?" + searchParams?.toString()).then(
      (result) => result.data
    );
  }

  ids(): Promise<string[]> {
    return API.get(this.path + "/ids").then((response) => response.data);
  }
}

class AdapterApiComponent extends ApiComponent<Adapter> {
  constructor() {
    super("v4/adapter");
  }

  copy(id: number): Promise<Adapter> {
    return API.post(this.path + "/" + id.toString() + "/copy").then(
      (response) => response.data
    );
  }

  integration(id: number): Promise<Integration[]> {
    return API.get(this.path + "/" + id.toString() + "/code").then(
      (response) => response.data
    );
  }

  addIntegration(id: number, model: Integration): Promise<Integration[]> {
    return API.post(this.path + "/" + id.toString() + "/code", model).then(
      (response) => response.data
    );
  }

  updateIntegration(
    id: number,
    integrationId: number,
    model: Integration
  ): Promise<Integration[]> {
    return API.put(
      this.path + "/" + id.toString() + "/code/" + integrationId.toString(),
      model
    ).then((response) => response.data);
  }

  removeIntegration(id: number, integratonId: number): Promise<Integration[]> {
    return API.delete(
      this.path + "/" + id.toString() + "/code/" + integratonId.toString()
    ).then((response) => response.data);
  }

  spmIntegrations(id: number): Promise<SpmIntegrationInfo[]> {
    return API.get(this.path + "/" + id.toString() + "/spm").then(
      (response) => response.data
    );
  }

  addSpmIntegration(
    id: number,
    model: SpmIntegrationDraft
  ): Promise<SpmIntegrationInfo> {
    return API.post(this.path + "/" + id.toString() + "/spm", model).then(
      (response) => response.data
    );
  }

  updateSpmIntegration(
    id: number,
    spmId: number,
    model: SpmIntegrationDraft
  ): Promise<SpmIntegrationInfo> {
    return API.put(
      this.path + "/" + id.toString() + "/spm/" + spmId.toString(),
      model
    ).then((response) => response.data);
  }

  removeSpmIntegration(id: number, spmId: number): Promise<void> {
    return API.delete(
      this.path + "/" + id.toString() + "/spm/" + spmId.toString()
    ).then((response) => response.data);
  }

  sdks(id: number): Promise<Sdk[]> {
    return API.get(this.path + "/" + id.toString() + "/sdk").then(
      (response) => response.data
    );
  }

  addSdk(id: number, model: Sdk): Promise<Sdk[]> {
    return API.post(this.path + "/" + id.toString() + "/sdk", model).then(
      (response) => response.data
    );
  }

  removeSdk(id: number, sdkId: number): Promise<Sdk[]> {
    return API.delete(
      this.path + "/" + id.toString() + "/sdk/" + sdkId.toString()
    ).then((response) => response.data);
  }

  // constraints_v2 is authoritative post-cutover (SDK-679). "manual" rows are editable via
  // addConstraintV2/removeConstraintV2; "crawler" rows are derived and read-only in this UI.
  // **Corrected 2026-09-22** (adapter full-CRUD task, "curated/lock switch" explicitly rejected):
  // the "crawler rows are ... read-only" half above is wrong — updateConstraintV2/
  // removeConstraintV2 now work for every row regardless of `source`. `source` is a normal,
  // user-editable field on `ConstraintV2Form` (create and edit alike) and is sent explicitly in
  // both the POST and PUT bodies — it is not left implicit or pinned by the client.
  constraintsV2(id: number): Promise<ConstraintV2[]> {
    return API.get(this.path + "/" + id.toString() + "/constraints/v2").then(
      (response) => response.data
    );
  }

  addConstraintV2(id: number, model: ConstraintV2Draft): Promise<ConstraintV2> {
    return API.post(
      this.path + "/" + id.toString() + "/constraints/v2",
      model
    ).then((response) => response.data);
  }

  updateConstraintV2(
    id: number,
    constraintId: number,
    model: ConstraintV2Draft
  ): Promise<ConstraintV2> {
    return API.put(
      this.path +
        "/" +
        id.toString() +
        "/constraints/v2/" +
        constraintId.toString(),
      model
    ).then((response) => response.data);
  }

  removeConstraintV2(id: number, constraintId: number): Promise<void> {
    return API.delete(
      this.path +
        "/" +
        id.toString() +
        "/constraints/v2/" +
        constraintId.toString()
    ).then((response) => response.data);
  }

  suggestedSdks(id: number): Promise<Sdk[]> {
    return API.get(this.path + "/" + id.toString() + "/sdk/suggested").then(
      (response) => response.data
    );
  }

  badges(id: number): Promise<Badge[]> {
    return API.get("v4/adapter/" + id.toString() + "/badge").then(
      (response) => response.data
    );
  }
}

class BadgeApiComponent {
  readonly path = "v4/badge";

  create(badge: Badge): Promise<Badge> {
    return API.post(this.path, badge).then((response) => response.data);
  }

  delete(id: number): Promise<void> {
    return API.delete(this.path + "/" + id.toString());
  }
}

class CrawlerApiComponent {
  readonly path = "v4/crawler";

  // Does NOT create a run record in System Health — only the full mediation reindex does.
  spmCrawl(provider: string): Promise<SpmCrawlResult> {
    return API.post(this.path + "/spm/" + provider).then(
      (response) => response.data
    );
  }

  reindex(mediation: string, platform: Platform): Promise<void> {
    return API.post(this.path + "/cron/reindex/" + mediation + "/" + platform.toString());
  }

  synchronize(
    mediation: string,
    platform: Platform,
    repository?: string,
    version?: string
  ): Promise<void> {
    const params = new URLSearchParams();
    if (repository) {
      params.append("owner", repository.split("/")[0]);
      params.append("repo", repository.split("/")[1]);
    }

    if (version) {
      params.append("version", version);
    }

    return API.post(
      this.path +
        "/" +
        mediation +
        "/" +
        platform.toString() +
        "?" +
        params.toString()
    );
  }

  adapter(
    platform: Platform,
    values: {
      mediation: string;
      sdk: string;
      platform: Platform;
      version?: string;
      pod?: string;
      group?: string;
      artifact?: string;
      repository?: string;
    }
  ): Promise<Adapter> {
    const params = new URLSearchParams();
    if (values.repository) {
      const repoPath = values.repository.replace(/^https?:\/\//, ""); // remove protocol

      if (platform === Platform.IOS) {
        // iOS (CocoaPods) - GitHub repo formats:
        // - "owner/repo"
        // - "github.com/owner/repo"
        // - "https://github.com/owner/repo.git"
        const cleanPath = repoPath
          .replace(/\.git$/, "") // remove .git suffix
          .replace(/^github\.com\//, ""); // remove github.com/

        const parts = cleanPath.split("/");
        if (parts.length >= 2) {
          params.append("owner", parts[0]);
          params.append("repo", parts[1]);
        }
      } else {
        // Android (Maven) - full repository URL formats:
        // - "artifactory.appodeal.com/appodeal"
        // - "https://artifactory.appodeal.com/appodeal"
        // Backend builds: https://${owner}/${repo}
        const firstSlash = repoPath.indexOf("/");
        if (firstSlash > 0) {
          params.append("owner", repoPath.substring(0, firstSlash));
          params.append("repo", repoPath.substring(firstSlash + 1));
        }
      }
    }

    if (values.pod) {
      params.append("pod", values.pod);
    }

    if (values.group) {
      params.append("group", values.group);
    }

    if (values.artifact) {
      params.append("artifact", values.artifact);
    }

    if (values.version) {
      params.append("version", values.version);
    }

    const endpoint =
      this.path +
      "/any/" +
      platform.toString() +
      "/" +
      values.mediation +
      "/" +
      values.sdk +
      "?" +
      params.toString();

    console.log(endpoint);
    return API.post(endpoint).then((response) => response.data);
  }
}

interface ResolvedRelease {
  adapters: Adapter[];
  sdks: Sdk[];
}

class ReleaseApiComponent {
  readonly path = "v4/release";

  resolved(sdk: Sdk): Promise<ResolvedRelease> {
    return API.get(
      [this.path, sdk.status, sdk.platform.toString(), sdk.version].join("/")
    ).then((response) => response.data);
  }

  plugin(
    plugin: Plugin
  ): Promise<{ ios: ResolvedRelease; android: ResolvedRelease }> {
    return API.get(
      [
        this.path,
        "plugin",
        plugin.framework,
        plugin.status,
        plugin.version,
      ].join("/")
    ).then((response) => response.data);
  }

  integration(sdk: Sdk, adapters: Adapter[], language: string) {
    const searchParams = new URLSearchParams();
    adapters.forEach((adapter) => {
      searchParams.append("adapter", adapter.id!.toString());
    });

    return API.get(
      this.path +
        "/" +
        sdk.status +
        "/" +
        sdk.platform.toString() +
        "/" +
        sdk.version +
        "/" +
        language +
        "?" +
        searchParams.toString()
    );
  }

  pluginIntegration(plugin: Plugin, adapters: Adapter[]) {
    const searchParams = new URLSearchParams();
    adapters.forEach((adapter) => {
      searchParams.append("adapter", adapter.id!.toString());
    });

    return API.get(
      [
        this.path,
        "plugin",
        plugin.framework,
        plugin.status,
        plugin.version,
        "dependencies",
      ].join("/") +
        "?" +
        searchParams.toString()
    );
  }
}

class PluginApiComponent extends ApiComponent<Plugin> {
  constructor() {
    super("v4/plugin");
  }

  copy(id: number): Promise<Plugin> {
    return API.post(this.path + "/" + id.toString() + "/copy").then(
      (response) => response.data
    );
  }

  sdks(id: number): Promise<Sdk[]> {
    return API.get(this.path + "/" + id.toString() + "/sdk").then(
      (response) => response.data
    );
  }

  addSdk(id: number, model: Sdk): Promise<Sdk[]> {
    return API.post(this.path + "/" + id.toString() + "/sdk", model).then(
      (response) => response.data
    );
  }

  suggestedSdks(id: number): Promise<Sdk[]> {
    return API.get(this.path + "/" + id.toString() + "/sdk/suggested").then(
      (response) => response.data
    );
  }

  removeSdk(id: number, sdkId: number): Promise<Sdk[]> {
    return API.delete(
      this.path + "/" + id.toString() + "/sdk/" + sdkId.toString()
    ).then((response) => response.data);
  }
}

class CrawlerMonitorApiComponent {
  readonly path = "v4/crawler";

  health(): Promise<HealthResponse> {
    return API.get(`${this.path}/health`).then((response) => response.data);
  }

  history(limit: number = 20, offset: number = 0): Promise<HistoryResponse> {
    return API.get(`${this.path}/history?limit=${limit}&offset=${offset}`).then(
      (response) => response.data
    );
  }

  runDetail(runId: number): Promise<CrawlerRunDetail> {
    return API.get(`${this.path}/history/${runId}`).then(
      (response) => response.data
    );
  }

  adapterResults(runId: number, providerResultId: number): Promise<CrawlerAdapterResultRecord[]> {
    return API.get(`${this.path}/history/${runId}/providers/${providerResultId}/adapters`).then(
      (response) => response.data
    );
  }

  trigger(): Promise<CrawlerRunDetail> {
    return API.post(`${this.path}/trigger`).then((response) => response.data);
  }
}

class VersionApiComponent {
  readonly path = "version";

  get(): Promise<VersionInfo> {
    return API.get(this.path).then((response) => response.data);
  }
}

class ValidatorApiComponent {
  readonly path = "v4/validator";

  versions(mediation: MediationType, platform: Platform): Promise<AvailableVersionsResponse> {
    return API.get(`${this.path}/versions/${mediation}/${platform}`).then(
      (response) => response.data
    );
  }

  validate(mediation: MediationType, platform: Platform, version: string): Promise<ValidationResponse> {
    return API.get(`${this.path}/validate/${mediation}/${platform}/${version}`).then(
      (response) => response.data
    );
  }
}

class RecommendationsApiComponent {
  readonly path = "v4/recommendations";

  getAppodealVersions(): Promise<AppodealVersionsResponse> {
    return API.get(`${this.path}/appodeal-versions`).then(
      (response) => response.data
    );
  }

  getRecommendations(appodealSdkId: number): Promise<RecommendationsResponse> {
    return API.get(`${this.path}/${appodealSdkId}`).then(
      (response) => response.data
    );
  }

  create(request: CreateRecommendationRequest): Promise<void> {
    return API.post(this.path, request);
  }

  delete(recommendationId: number): Promise<void> {
    return API.delete(`${this.path}/${recommendationId}`);
  }

  copyFrom(appodealSdkId: number, sourceSdkId: number): Promise<CopyRecommendationsResponse> {
    return API.post(`${this.path}/${appodealSdkId}/copy-from/${sourceSdkId}`).then(
      (response) => response.data
    );
  }
}

/**
 * Read-only browse of the MATERIALIZED SPM table (`v4/spm/integrations`).
 *
 * Deliberately NOT an `ApiComponent` subclass: the materialized rows are written exclusively by
 * the SPM crawler and have no create/update/delete contract. Inheriting `ApiComponent` would
 * expose `create`/`update`/`delete` that resolve to `v4/spm/integrations/{id}` — paths the
 * backend does not serve, and which would read as "SPM rows are editable here" when they are not.
 */
class SpmIntegrationApiComponent {
  readonly path = "v4/spm/integrations";

  all(searchParams?: URLSearchParams): Promise<PageResult<SpmIntegrationRecord>> {
    return API.get(this.path + "?" + (searchParams?.toString() ?? "")).then(
      (result) => result.data
    );
  }
}

/**
 * Android 3-step dependency API (`v4/android`) — SDK-562 Requirement W (Integration Preview).
 * Confirmed by grep: nothing in `webapp/src` called `v4/android` before this task, so this is
 * built directly from the backend DTOs (`NativeModels.kt`, `IntegrationRoutesResources.kt`), not
 * adapted from a working pattern. Every parameter is optional and omitted-by-default so the
 * default call (no options) is parameter-free — byte-identical to what an anonymous external
 * caller gets.
 */
class AndroidWizardApiComponent {
  readonly path = "v4/android";

  mediations(
    version: string,
    options?: { mediation?: string; recommended?: boolean }
  ): Promise<NativeMediationsResponse> {
    const params = new URLSearchParams();
    if (options?.recommended) params.set("recommended", "true");
    if (options?.mediation) params.set("mediation", options.mediation);
    const qs = params.toString();
    return API.get(
      `${this.path}/${version}/mediations${qs ? "?" + qs : ""}`
    ).then((response) => response.data);
  }

  sdks(
    version: string,
    selection: PlatformSelection,
    options?: { recommended?: boolean }
  ): Promise<NativeSdksResponse> {
    const params = new URLSearchParams();
    if (options?.recommended) params.set("recommended", "true");
    const qs = params.toString();
    return API.post(
      `${this.path}/${version}/sdks${qs ? "?" + qs : ""}`,
      selection
    ).then((response) => response.data);
  }

  /** Returns the raw Gradle snippet (`text/plain`) — never JSON. */
  dependencies(
    version: string,
    language: "kt" | "groovy",
    selection: PlatformSelection,
    options?: { mediation?: string }
  ): Promise<string> {
    const params = new URLSearchParams();
    if (options?.mediation) params.set("mediation", options.mediation);
    const qs = params.toString();
    return API.post(
      `${this.path}/${version}/dependencies/${language}${qs ? "?" + qs : ""}`,
      selection
    ).then((response) => response.data);
  }
}

/**
 * iOS 3-step dependency API (`v4/ios`) — SDK-562 Requirement W + B2 (native SPM). Same
 * from-scratch situation as `AndroidWizardApiComponent`. `integrationType` is only sent when it is
 * `"spm"` — omitted (not even `"cocoapods"`) keeps the default call parameter-free, matching the
 * backend's own `cocoapods` default byte-for-byte.
 */
class IosWizardApiComponent {
  readonly path = "v4/ios";

  mediations(
    version: string,
    options?: {
      mediation?: string;
      recommended?: boolean;
      integrationType?: IntegrationType;
    }
  ): Promise<NativeMediationsResponse> {
    const params = new URLSearchParams();
    if (options?.recommended) params.set("recommended", "true");
    if (options?.mediation) params.set("mediation", options.mediation);
    if (options?.integrationType === "spm") {
      params.set("integration_type", options.integrationType);
    }
    const qs = params.toString();
    return API.get(
      `${this.path}/${version}/mediations${qs ? "?" + qs : ""}`
    ).then((response) => response.data);
  }

  sdks(
    version: string,
    selection: PlatformSelection,
    options?: { recommended?: boolean; integrationType?: IntegrationType }
  ): Promise<NativeSdksResponse> {
    const params = new URLSearchParams();
    if (options?.recommended) params.set("recommended", "true");
    if (options?.integrationType === "spm") {
      params.set("integration_type", options.integrationType);
    }
    const qs = params.toString();
    return API.post(
      `${this.path}/${version}/sdks${qs ? "?" + qs : ""}`,
      selection
    ).then((response) => response.data);
  }

  /**
   * `integration_type` absent/`"cocoapods"` resolves to a `text/plain` Podfile snippet;
   * `"spm"` resolves to a flat `SpmDependency[]` (`application/json`, T5). The caller knows which
   * one it asked for via `options.integrationType`.
   */
  dependencies(
    version: string,
    selection: PlatformSelection,
    options?: { mediation?: string; integrationType?: IntegrationType }
  ): Promise<string | SpmDependency[]> {
    const params = new URLSearchParams();
    if (options?.mediation) params.set("mediation", options.mediation);
    if (options?.integrationType === "spm") {
      params.set("integration_type", options.integrationType);
    }
    const qs = params.toString();
    return API.post(
      `${this.path}/${version}/dependencies${qs ? "?" + qs : ""}`,
      selection
    ).then((response) => response.data);
  }
}

/**
 * Unity 3-step dependency API (`v4/unity`) — only the subset Requirement W needs
 * (`mediations`/`sdks`/`dependencies`). Distinct from `WizardService.Plugin` (mapping-scoped CRUD
 * on `Plugin` releases, `v4/plugin`) and `WizardService.Release` (legacy `v4/release` family,
 * untouched) — named `UnityWizard` to avoid clashing with either.
 */
class UnityWizardApiComponent {
  readonly path = "v4/unity";

  mediations(
    version: string,
    options?: { recommended?: boolean }
  ): Promise<UnityMediationsResponse> {
    const params = new URLSearchParams();
    if (options?.recommended) params.set("recommended", "true");
    const qs = params.toString();
    return API.get(
      `${this.path}/${version}/mediations${qs ? "?" + qs : ""}`
    ).then((response) => response.data);
  }

  sdks(
    version: string,
    request: SdksRequest,
    options?: { recommended?: boolean }
  ): Promise<UnitySdksResponse> {
    const params = new URLSearchParams();
    if (options?.recommended) params.set("recommended", "true");
    const qs = params.toString();
    return API.post(
      `${this.path}/${version}/sdks${qs ? "?" + qs : ""}`,
      request
    ).then((response) => response.data);
  }

  /** Returns the EDM4U dependencies XML (`application/xml`), including `<remoteSwiftPackage>`. */
  dependencies(version: string, request: SdksRequest): Promise<string> {
    return API.post(`${this.path}/${version}/dependencies`, request).then(
      (response) => response.data
    );
  }
}

/**
 * `POST /v4/spm/coverage` (SDK-562 O1) — the Integration Preview's validator panel data source.
 * Auth-gated by `authenticate("okta-session")` on the backend (same block as `/v4/spm`), so this
 * is internal-only by construction. Unlike `/v4/{android,ios,unity}`, `mediation` here is not a
 * general opt-in — it exists solely so the backend's core-SDK lookup can be pointed at a
 * non-`appodeal` root (e.g. MAX-rooted pages), same idiom as those other routes.
 */
class SpmCoverageApiComponent {
  readonly path = "v4/spm/coverage";

  /**
   * Body omitted → global V2 report, `v1: null`. Body present → V1 (iOS) + V2 scoped to it.
   * `mediation` (query param, not body) → the selection's root iOS mediation status; omitted
   * means `appodeal`, matching the backend default byte-for-byte.
   */
  check(selection?: SdksRequest, mediation?: string): Promise<SpmCoverageResponse> {
    const params = new URLSearchParams();
    if (mediation) params.set("mediation", mediation);
    const qs = params.toString();
    return API.post(`${this.path}${qs ? "?" + qs : ""}`, selection).then(
      (response) => response.data
    );
  }
}

export const WizardService = {
  Mapping: new ApiComponent<Mapping>("v4/status"),
  SpmInfo: new ApiComponent<SpmInfo>("v4/spm"),
  SpmIntegration: new SpmIntegrationApiComponent(),
  CrawlerMonitor: new CrawlerMonitorApiComponent(),
  Sdk: new SdkApiComponent(),
  Adapter: new AdapterApiComponent(),
  Badge: new BadgeApiComponent(),
  Crawler: new CrawlerApiComponent(),
  Release: new ReleaseApiComponent(),
  SKAdNetwork: new SKAdNetworkApiComponent(),
  AdAttributionKit: new AdAttributionKitApiComponent(),
  Plugin: new PluginApiComponent(),
  Version: new VersionApiComponent(),
  Validator: new ValidatorApiComponent(),
  Recommendations: new RecommendationsApiComponent(),
  Android: new AndroidWizardApiComponent(),
  Ios: new IosWizardApiComponent(),
  UnityWizard: new UnityWizardApiComponent(),
  SpmCoverage: new SpmCoverageApiComponent(),
};
